import api from "@/api/apiAlarm";
import { convertDate } from "@/utils/localeTime";

export default {
  namespaced: true,

  state() {
    return {
      // 라이트 알람 API에서 사용하는 기본 routeName
      routeName: "signal-light-alarm",
      // routeName: "/signal-light",

      // 알람 ON/OFF 상태
      alarmStatus: null,

      // 알람음 목록
      alarmList: [],

      // 타입별 전체 신호 개수
      lightCount: {
        bullish: 0,
        standby: 0,
        bearish: 0,
      },

      // API에서 받은 전체 histories
      // 메인에서는 검색 개수 계산용으로 사용
      // 상세에서는 type/search 필터링용으로 사용
      lightHistory: [],

      // 상단에 보여줄 변환된 시간 문자열
      dateConvert: "",

      // API 로딩 완료 여부
      loadApi: false,
    };
  },

  getters: {
    // route query type 값 0/1/2를 실제 API type 문자열로 변환
    selectedLightType: () => (type) => {
      const lightTypeMap = {
        0: "bullish",
        1: "standby",
        2: "bearish",
      };

      return lightTypeMap[String(type)];
    },

    // 상세 페이지 상단에 보여줄 현재 type의 전체 개수
    getLightCountByType: (state, getters) => (type) => {
      const selectedType = getters.selectedLightType(type);

      return state.lightCount[selectedType] || 0;
    },

    // 상세 페이지에서 현재 type + 검색어에 맞는 리스트만 반환
    getHistoryByTypeAndSearch: (state, getters) => ({ type, search }) => {
      const selectedType = getters.selectedLightType(type);
      const keyword = String(search || "").trim().toLowerCase();

      return state.lightHistory
        .filter((history) => {
          return String(history.type || "").toLowerCase() === selectedType;
        })
        .filter((history) => {
          if (!keyword) return true;

          return String(history.coin || "").toLowerCase().includes(keyword);
        });
    },

    // 메인 페이지에서 검색어가 있을 때 bullish/standby/bearish 각각 몇 개 검색됐는지 계산
    getSearchCount: (state) => (search) => {
      const keyword = String(search || "").trim().toLowerCase();

      const result = {
        bullish: 0,
        standby: 0,
        bearish: 0,
      };

      // 검색어가 없으면 검색 개수는 계산하지 않음
      if (!keyword) return result;

      state.lightHistory.forEach((history) => {
        const coin = String(history.coin || "").toLowerCase();
        const type = String(history.type || "").toLowerCase();

        // 코인명이 검색어를 포함하지 않으면 제외
        if (!coin.includes(keyword)) return;

        // 검색된 항목을 type별로 카운트
        if (type === "bullish") result.bullish += 1;
        if (type === "standby") result.standby += 1;
        if (type === "bearish") result.bearish += 1;
      });

      return result;
    },
  },

  mutations: {
    // 알람 상태 저장
    SET_ALARM_STATUS(state, payload) {
      state.alarmStatus = payload;
    },

    // 알람음 목록 저장
    SET_ALARM_LIST(state, payload) {
      state.alarmList = payload || [];
    },

    // 타입별 전체 카운트 저장
    SET_LIGHT_COUNT(state, payload) {
      state.lightCount = {
        bullish: payload?.bullish || 0,
        standby: payload?.standby || 0,
        bearish: payload?.bearish || 0,
      };
    },

    // 전체 히스토리 저장
    SET_LIGHT_HISTORY(state, payload) {
      state.lightHistory = payload || [];
    },

    // 변환된 날짜 문자열 저장
    SET_DATE_CONVERT(state, payload) {
      state.dateConvert = payload || "";
    },

    // API 로딩 상태 저장
    SET_LOAD_API(state, payload) {
      state.loadApi = payload;
    },
  },

  actions: {
    // 알람 ON/OFF 상태 가져오기
    async getAlarmStatus({ commit }, routeName) {
      const status = await api.getAlarms(routeName);

      commit("SET_ALARM_STATUS", status.data);
    },

    // 알람음 목록 가져오기
    // t는 컴포넌트에서 useI18n()으로 받은 번역 함수
    async setAlarmList({ commit }, t) {
      const alarms = await api.getAlarmSound();

      const alarmList = (alarms.data || []).map((alarm, index) => {
        if (alarm.filename === "default") {
          return t("alarm.price.default");
        }

        return t("alarm.price.soundFormat", { number: index });
      });

      commit("SET_ALARM_LIST", alarmList);
    },

    // 라이트 히스토리 + 타입별 카운트 + 시간 가져오기
    async setHistoryList({ state, commit }) {
        commit("SET_LOAD_API", false);

        const historyData = await api.getHistory(`${state.routeName}-histories`);
        // const historyData = await api.getHistory(`signal/filter25-summary`);

        const data = historyData.data || [];

        // API에서 받은 타입별 개수 저장
        commit("SET_LIGHT_COUNT", {
            bullish: data.bullish,
            standby: data.standby,
            bearish: data.bearish,
        });

        let dateText = "";
        let dateInfo = null;

        // regDate가 있으면 화면용 날짜로 변환
        if (data.regDate) {
            dateInfo = convertDate(data.regDate);
            dateText = `${dateInfo.Date} ${dateInfo.alarmTime} ${dateInfo.utc}`;

            commit("SET_DATE_CONVERT", dateText);
        } else {
            commit("SET_DATE_CONVERT", "");
        }

        const histories = data.histories || [];
        // const histories = [
        //     {exchange: "Binance", quote: "USDT", coin: "BTC", type: "bullish", go: true}, 
        //     {exchange: "Binance", quote: "USDT", coin: "XRP", type: "bullish", go: false},
        //     {exchange: "Binance", quote: "USDT", coin: "BTC", type: "standby", go: true}, 
        //     {exchange: "Binance", quote: "USDT", coin: "ETH", type: "standby", go: false},
        //     {exchange: "Binance", quote: "USDT", coin: "BTC", type: "bearish", go: true}, 
        //     {exchange: "Binance", quote: "USDT", coin: "SOL", type: "bearish", go: false}
        // ]

        // 상세 리스트에서 바로 Date/Time/utcTime을 쓸 수 있도록 각 history에 날짜 정보 붙임
        const formattedHistories = histories.map((history) => ({
            ...history,
            Date: dateInfo?.Date || "",
            Time: dateInfo?.alarmTime || "",
            utcTime: dateInfo?.utc || "",
        }));

        commit("SET_LIGHT_HISTORY", formattedHistories);
        commit("SET_LOAD_API", true);
    },
  },
};